import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { MessageSquareText, Save, Coins, Send } from "lucide-react";
import { smsStats } from "../lib/sms";

// Impostazioni SMS Factor: token API (cifrato), mittente, crediti e SMS di prova
export default function SmsFactorCard() {
  const [st, setSt] = useState(null);
  const [form, setForm] = useState({ token: "", sender: "", enabled: true, pushtype: "marketing" });
  const [test, setTest] = useState({ phone: "", text: "Test SMS da Vivi Fidelity Club" });
  const [busy, setBusy] = useState(null);
  const stats = smsStats(test.text);

  const load = async () => {
    try { const { data } = await api.get("/sms/config"); setSt(data); setForm({ token: "", sender: data.sender || "", enabled: data.enabled !== false, pushtype: data.pushtype || "marketing" }); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy("save");
    try { const { data } = await api.put("/sms/config", form); setSt(data); setForm((f) => ({ ...f, token: "" })); toast.success("Impostazioni SMS salvate"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };
  const credits = async () => {
    setBusy("credits");
    try { const { data } = await api.get("/sms/credits"); toast.success(`Crediti SMS Factor: ${data.unlimited ? "illimitati" : data.credits}`); await load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };
  const sendTest = async () => {
    setBusy("test");
    try { const { data } = await api.post("/sms/test", test); toast.success(`SMS inviato (ticket ${data.ticket || "—"}, costo ${data.cost ?? "?"} crediti)`); await load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };

  if (!st) return null;
  return (
    <div className="card-elev p-5 space-y-4" data-testid="smsfactor-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><MessageSquareText className="h-5 w-5" /> SMS Factor</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Invio SMS per le campagne (max {st.max_chars} caratteri). Crea il token su smsfactor.com → Developers → API tokens.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className={`pill ${st.configured ? "pill-mint" : "pill-muted"}`} data-testid="sms-status">{st.configured ? "Configurato" : "Non configurato"}</span>
          {st.last_credits != null && <span className="pill pill-sky" data-testid="sms-credits"><Coins className="h-3 w-3" /> {st.last_credits} crediti</span>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <Label className="text-zinc-300 mb-1 inline-block">Token API</Label>
          <Input type="password" data-testid="sms-token" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })}
            placeholder={st.configured ? `Salvato (${st.token_masked}) — lascia vuoto per non cambiarlo` : "Incolla il token SMS Factor"} className="bg-zinc-950 border-zinc-800 font-mono" />
        </div>
        <div>
          <Label className="text-zinc-300 mb-1 inline-block">Mittente (max 11)</Label>
          <Input data-testid="sms-sender" maxLength={11} value={form.sender} onChange={(e) => setForm({ ...form, sender: e.target.value })} placeholder="es. VIVICLUB" className="bg-zinc-950 border-zinc-800 font-mono" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="sms-enabled" className="h-4 w-4" /> Canale SMS attivo per le campagne</label>
        <Button size="sm" disabled={busy === "save"} onClick={save} data-testid="sms-save" className="h-9 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"><Save className="h-3.5 w-3.5 mr-1" /> Salva</Button>
        <Button size="sm" variant="outline" disabled={!st.configured || busy === "credits"} onClick={credits} data-testid="sms-check-credits" className="h-9 border-zinc-800"><Coins className="h-3.5 w-3.5 mr-1" /> Verifica crediti</Button>
      </div>
      <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3 space-y-2" data-testid="sms-test-block">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">SMS di prova</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input data-testid="sms-test-phone" value={test.phone} onChange={(e) => setTest({ ...test, phone: e.target.value })} placeholder="+39 3xx xxxxxxx" className="bg-zinc-900 border-zinc-800 font-mono" />
          <div className="sm:col-span-2">
            <Textarea data-testid="sms-test-text" rows={2} maxLength={stats.limit} value={test.text} onChange={(e) => setTest({ ...test, text: e.target.value })} className="bg-zinc-900 border-zinc-800 text-sm" />
            <div className={`text-[11px] mt-1 font-mono ${stats.ok ? "text-zinc-500" : "text-red-300"}`} data-testid="sms-test-counter">
              {stats.length}/{stats.limit} caratteri{stats.unicode ? " · caratteri speciali: limite 70" : ""}
            </div>
          </div>
        </div>
        <Button size="sm" variant="outline" disabled={!st.configured || !test.phone || !stats.ok || busy === "test"} onClick={sendTest} data-testid="sms-test-send" className="h-9 border-zinc-800"><Send className="h-3.5 w-3.5 mr-1" /> Invia SMS di prova</Button>
      </div>
    </div>
  );
}
